/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Box } from '@tishman/components';
import React, { forwardRef, useMemo, useState } from 'react';

import { SingleTicket } from '..';
import { putInArray } from '../../../services/viva/utils';
import {
  selectCityPassBooklet,
  selectFlow,
  useAppSelector,
} from '../../../store';
import {
  ADDITIONAL_EXPRESS_PASS_ADDONS_THAT_DONT_SHOW_TIME,
  ADDONS_THAT_DONT_SHOW_TIME,
  Flow,
  TICKETS_AND_ADDONS_THAT_DONT_SHOW_TIME,
  TOR_EXPRESS_PRODUCT_AK,
} from '../../../services/viva/constants';

import type { ComponentPropsWithRef } from 'react';
import type {
  ITEM,
  OrderResponse,
  ORDERTICKET,
} from '../../../services/viva/types/response';

interface TicketProps {
  order: OrderResponse;
}

export const PrintTicket = forwardRef(
  (
    { order }: TicketProps,
    ref?: ComponentPropsWithRef<'div'>['ref'],
  ): React.JSX.Element => {
    const reservationAk = order.SALE.RESERVATIONAK?.toString() ?? null;
    const citypassBooklet = useAppSelector(selectCityPassBooklet);
    const flow = useAppSelector(selectFlow);

    const [hasExpressPassTicket, setHasExpressPassTicket] =
      useState<boolean>(false);

    const tickets = useMemo(() => {
      const exemptTicketCodes = ['PROCESSING', 'PROCESSFEE', 'HANDLING'];

      return putInArray(order?.ITEMLIST.ITEM)
        .filter(
          (item: ITEM) => exemptTicketCodes.indexOf(item.PRODUCT.CODE) === -1,
        )
        .flatMap((item: ITEM) => {
          const performanceTime = putInArray(
            item?.ITEMPERFORMANCELIST?.ITEMPERFORMANCE?.DATETIME,
          )?.[0];

          if (item.PRODUCT.AK === TOR_EXPRESS_PRODUCT_AK)
            setHasExpressPassTicket(true);

          const showPerformanceTime =
            !ADDONS_THAT_DONT_SHOW_TIME.includes(item.PRODUCT.AK) ||
            (hasExpressPassTicket &&
              !ADDITIONAL_EXPRESS_PASS_ADDONS_THAT_DONT_SHOW_TIME.includes(
                item.PRODUCT.AK,
              )); // Some addons shouldnt show a time

          const orderTicket = putInArray(
            item?.ORDERTICKETLIST?.ORDERTICKET,
          ).map((ticket: ORDERTICKET) => ({
            order,
            ticket: {
              ...ticket,
              item,
              performanceTime: showPerformanceTime ? performanceTime : {},
              dateOnly:
                TICKETS_AND_ADDONS_THAT_DONT_SHOW_TIME.includes(
                  item.PRODUCT.AK,
                ) ||
                (hasExpressPassTicket &&
                  ADDITIONAL_EXPRESS_PASS_ADDONS_THAT_DONT_SHOW_TIME.includes(
                    item.PRODUCT.AK,
                  )),
            },
          }));

          return orderTicket;
        });
    }, [order, hasExpressPassTicket]);

    return (
      <Box
        ref={ref}
        sx={{
          'mt': 5,
          '@media print': {
            mt: 0,
          },
        }}
      >
        {tickets.map(({ order, ticket }, index) => {
          let _reservationAk = reservationAk;

          if (
            flow === Flow.CITY_PASS_REDEMPTION &&
            citypassBooklet &&
            citypassBooklet.length > 0
          ) {
            _reservationAk = citypassBooklet[index]?.voucherCode?.toString();
          }

          return (
            <Box
              key={`${order.SALE.AK}-${ticket?.TICKETID}`}
              sx={{
                '@media print': {
                  mt: '-10mm',
                  transform: 'scale(0.75)',
                  height: '100vh',
                  maxHeight: '100vh',
                  pageBreakAfter: 'always',
                  breakAfter: 'page',
                },
              }}
            >
              <SingleTicket
                order={order}
                reservationAk={_reservationAk}
                ticket={ticket}
              />
            </Box>
          );
        })}
      </Box>
    );
  },
);

PrintTicket.displayName = 'PrintTicket';
